import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, useCallback, useEffect, useState } from 'react';
import { useAuthStore } from '../../stores';
import { useTranslation } from '../../i18n';
import { deleteVectorPackage, fetchVectorPackageFiles, fetchVectorPackages, importGitLibrary, resolveAssetUrl, uploadVectorPackage, } from '../../util/api';
import './Admin.css';
export function VectorPackages() {
    const { t } = useTranslation();
    const { accessToken } = useAuthStore();
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [name, setName] = useState('');
    const [file, setFile] = useState(null);
    const [fileInputKey, setFileInputKey] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [gitUrl, setGitUrl] = useState('');
    const [gitBranch, setGitBranch] = useState('main');
    const [gitPath, setGitPath] = useState('kits');
    const [gitFormat, setGitFormat] = useState('svg');
    const [importing, setImporting] = useState(false);
    const [notice, setNotice] = useState('');
    const [deletingId, setDeletingId] = useState(null);
    const [previewId, setPreviewId] = useState(null);
    const [previewFiles, setPreviewFiles] = useState([]);
    const [previewLoading, setPreviewLoading] = useState(false);
    const loadPackages = useCallback(async () => {
        setError('');
        try {
            const data = await fetchVectorPackages(accessToken);
            setPackages(data);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : t('admin.vectorPackages.loadFailed'));
        }
        finally {
            setLoading(false);
        }
    }, [accessToken, t]);
    useEffect(() => {
        void loadPackages();
    }, [loadPackages]);
    const handleUpload = async (event) => {
        event.preventDefault();
        setError('');
        if (!name.trim()) {
            setError(t('admin.vectorPackages.nameRequired'));
            return;
        }
        if (!file) {
            setError(t('admin.vectorPackages.fileRequired'));
            return;
        }
        setUploading(true);
        try {
            await uploadVectorPackage(name.trim(), file, accessToken);
            setName('');
            setFile(null);
            setFileInputKey((key) => key + 1);
            await loadPackages();
        }
        catch (err) {
            setError(err instanceof Error ? err.message : t('admin.vectorPackages.uploadFailed'));
        }
        finally {
            setUploading(false);
        }
    };
    const handleGitImport = async (event) => {
        event.preventDefault();
        if (!gitUrl.trim()) {
            setError(t('admin.vectorPackages.gitUrlRequired'));
            return;
        }
        setImporting(true);
        setError('');
        setNotice('');
        try {
            const imported = await importGitLibrary({
                url: gitUrl.trim(),
                branch: gitBranch.trim() || 'main',
                path: gitPath.trim(),
                format: gitFormat,
            }, accessToken);
            setNotice(t('admin.vectorPackages.gitImported', { count: imported.length }));
            await loadPackages();
        }
        catch (err) {
            setError(err instanceof Error ? err.message : t('admin.vectorPackages.gitFailed'));
        }
        finally {
            setImporting(false);
        }
    };
    const handleDelete = async (pkg) => {
        if (!window.confirm(t('admin.vectorPackages.deleteConfirm', { name: pkg.name })))
            return;
        setDeletingId(pkg.id);
        setError('');
        try {
            await deleteVectorPackage(pkg.id, accessToken);
            if (previewId === pkg.id) {
                setPreviewId(null);
                setPreviewFiles([]);
            }
            await loadPackages();
        }
        catch (err) {
            setError(err instanceof Error ? err.message : t('admin.vectorPackages.deleteFailed'));
        }
        finally {
            setDeletingId(null);
        }
    };
    const togglePreview = async (packageId) => {
        if (previewId === packageId) {
            setPreviewId(null);
            setPreviewFiles([]);
            return;
        }
        setPreviewId(packageId);
        setPreviewLoading(true);
        setPreviewFiles([]);
        try {
            const files = await fetchVectorPackageFiles(packageId, accessToken);
            setPreviewFiles(files);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : t('admin.vectorPackages.loadFailed'));
        }
        finally {
            setPreviewLoading(false);
        }
    };
    if (loading) {
        return _jsx("div", { className: "admin-loading", children: t('common.loading') });
    }
    return (_jsxs("div", { className: "admin-page", children: [_jsx("header", { className: "admin-page-header", children: _jsxs("div", { children: [_jsx("h1", { children: t('admin.vectorPackages.title') }), _jsx("p", { children: t('admin.vectorPackages.description') })] }) }), error ? _jsx("div", { className: "admin-error", children: error }) : null, notice ? _jsx("div", { className: "admin-muted-text", children: notice }) : null, _jsx("section", { className: "admin-card", children: _jsxs("form", { className: "admin-form", onSubmit: (event) => void handleGitImport(event), children: [_jsxs("div", { className: "admin-form-row", children: [_jsxs("label", { className: "admin-form-label", children: [t('admin.vectorPackages.gitUrl'), _jsx("input", { className: "admin-form-input", value: gitUrl, onChange: (event) => setGitUrl(event.target.value), placeholder: "https://github.com/org/repo", required: true })] }), _jsxs("label", { className: "admin-form-label", children: [t('admin.vectorPackages.gitBranch'), _jsx("input", { className: "admin-form-input", value: gitBranch, onChange: (event) => setGitBranch(event.target.value), maxLength: 255 })] })] }), _jsxs("div", { className: "admin-form-row", children: [_jsxs("label", { className: "admin-form-label", children: [t('admin.vectorPackages.gitPath'), _jsx("input", { className: "admin-form-input", value: gitPath, onChange: (event) => setGitPath(event.target.value), placeholder: "kits" })] }), _jsxs("label", { className: "admin-form-label", children: [t('admin.vectorPackages.gitFormat'), _jsxs("select", { className: "admin-form-input", value: gitFormat, onChange: (event) => setGitFormat(event.target.value === 'png' ? 'png' : 'svg'), children: [_jsx("option", { value: "svg", children: t('admin.vectorPackages.formatSvg') }), _jsx("option", { value: "png", children: t('admin.vectorPackages.formatPng') })] })] })] }), _jsx("p", { className: "admin-muted-text", children: t('admin.vectorPackages.gitHint') }), _jsx("div", { className: "admin-form-actions", children: _jsx("button", { className: "admin-btn admin-btn-primary", type: "submit", disabled: importing || uploading, children: importing ? t('admin.vectorPackages.gitImporting') : t('admin.vectorPackages.gitImport') }) })] }) }), _jsx("section", { className: "admin-card", children: _jsxs("form", { className: "admin-form", onSubmit: (event) => void handleUpload(event), children: [_jsxs("div", { className: "admin-form-row", children: [_jsxs("label", { className: "admin-form-label", children: [t('admin.vectorPackages.name'), _jsx("input", { className: "admin-form-input", value: name, onChange: (event) => setName(event.target.value), maxLength: 120, required: true })] }), _jsxs("label", { className: "admin-form-label", children: [t('admin.vectorPackages.file'), _jsx("input", { className: "admin-form-input", type: "file", accept: ".zip,.svg,.png,.webp,.jpg,.jpeg,.gif,image/svg+xml,application/zip", onChange: (event) => setFile(event.target.files?.[0] ?? null) }, fileInputKey)] })] }), _jsx("p", { className: "admin-muted-text", children: t('admin.vectorPackages.fileHint') }), _jsx("div", { className: "admin-form-actions", children: _jsx("button", { className: "admin-btn admin-btn-primary", type: "submit", disabled: uploading, children: uploading ? t('admin.vectorPackages.uploading') : t('admin.vectorPackages.upload') }) })] }) }), _jsxs("section", { className: "admin-table-section", children: [_jsx("div", { className: "admin-table-toolbar", children: _jsxs("h2", { children: [t('admin.vectorPackages.title'), " (", packages.length, ")"] }) }), packages.length === 0 ? (_jsx("div", { className: "admin-empty-state", children: _jsx("p", { children: t('admin.vectorPackages.empty') }) })) : (_jsx("div", { className: "admin-table-container", children: _jsxs("table", { className: "admin-table", children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: t('admin.vectorPackages.name') }), _jsx("th", { children: t('admin.vectorPackages.file') }), _jsx("th", { children: t('admin.vectorPackages.preview') }), _jsx("th", {})] }) }), _jsx("tbody", { children: packages.map((pkg) => (_jsxs(Fragment, { children: [_jsxs("tr", { children: [_jsx("td", { children: _jsx("span", { className: "provider-name", children: pkg.name }) }), _jsx("td", { children: pkg.id }), _jsx("td", { children: t('admin.vectorPackages.files', { count: pkg.fileCount }) }), _jsxs("td", { className: "admin-card-actions", children: [_jsx("button", { type: "button", className: "admin-btn admin-btn-secondary admin-btn-sm", onClick: () => void togglePreview(pkg.id), children: previewId === pkg.id
                                                                    ? t('admin.vectorPackages.hide')
                                                                    : t('admin.vectorPackages.preview') }), _jsx("button", { type: "button", className: "admin-btn admin-btn-danger admin-btn-sm", disabled: deletingId === pkg.id, onClick: () => void handleDelete(pkg), children: t('admin.vectorPackages.delete') })] })] }), previewId === pkg.id ? (_jsx("tr", { className: "admin-table-sub-row", children: _jsx("td", { colSpan: 4, children: previewLoading ? (_jsx("p", { className: "admin-muted-text", children: t('common.loading') })) : (_jsx("div", { className: "vector-file-grid", children: previewFiles.map((item) => (_jsxs("figure", { children: [_jsx("img", { src: resolveAssetUrl(item.url), alt: item.name }), _jsx("figcaption", { children: item.name })] }, item.id))) })) }) }, `${pkg.id}-files`)) : null] }, pkg.id))) })] }) }))] })] }));
}
